// /calendar — the shared planner (W6).
//
// Three surfaces live here, and the count has shrunk twice (DESIGN §2):
//
//   1. month view  — the default
//   2. day detail  — BottomSheet
//   3. filter      — BottomSheet (Q3, Q10)
//
// Cycle is its own route (`/calendar/cycle`, Q24) and the event editor left for
// `/calendar/events/new` + `/:id/edit` in W40.1 F7 — see ADR-0101. Both moves
// took work off this file, which is why it now clears the §3 #10 ceiling.
//
// Desktop (Q25): a single centred `max-w-[390px]` phone-width container and
// nothing else. The old 60/40 month-grid-plus-drawer contract is void
// (README §2.6 Q4) — DESIGN §5.2 explains why a day pane competes with the
// grid for the same visual channel, and the day detail reads better as prose
// in a sheet than as a cramped sidebar.
//
// Filter state lives in URL query params rather than component state so a
// filter survives a reload, a shared link, and the browser's back button
// (DESIGN §8 left this open; query params is the answer here because the
// filtered view is genuinely a different view, not a transient toggle).

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { ErrorState } from "@/components/primitives/ErrorState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useCalendarUndoBar } from "@/hooks/useCalendarUndoBar";

import { MonthGrid, WeekdayHeader } from "@/components/calendar/MonthGrid";import { CalendarMonthNav } from "@/components/calendar/CalendarMonthNav";
import { CalendarStatusBar } from "@/components/calendar/CalendarStatusBar";
import { DaySheet } from "@/components/calendar/DaySheet";
import { MonthLegend } from "@/components/calendar/MonthLegend";
import { FilterPanel } from "@/components/calendar/FilterPanel";
import { useCalendarFilters } from "@/hooks/useCalendarFilters";
import { useCalendarNavigation } from "@/hooks/useCalendarNavigation";

import {
  getDay,
  getMonth,
  type SharedSegment,
} from "@/lib/api/calendar";
import { calendarMonthSummary } from "@/lib/heroSummary";

function monthOf(date: Date): { year: number; month: number } {
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

function shiftMonth(year: number, month: number, delta: number) {
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

export function CalendarPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const now = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => {
    const y = Number(params.get("y"));
    const m = Number(params.get("m"));
    return y && m ? { year: y, month: m } : monthOf(now);
  });

  // W58.1 — 篩選狀態與 URL 的雙向同步搬到 `useCalendarFilters`：
  // `routes/Calendar.tsx` 卡在 §3 #10 的 15 KB 上限上，而這段機械性的
  // 同步邏輯不是版面。檔案裡早就有同一條規矩的先例（`@/lib/heroSummary`）。
  const { filter, setFilter } = useCalendarFilters();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  // `"event"` left with W40.1 F7: the create / edit form is a route now, so the
  // only sheets on this page are the day sheet and the filter. Deleting went
  // with it — the form sheet was the month view's only delete affordance, and
  // `/calendar/events/:id` has always had its own (with its own undo bar).
  const [sheet, setSheet] = useState<"none" | "day" | "filter">("none");

  const monthQuery = useQuery({
    queryKey: [
      "calendar-month",
      cursor.year,
      cursor.month,
      filter.categories.join(","),
      filter.owner,
      filter.maxPerDay,
    ],
    queryFn: () =>
      getMonth({
        year: cursor.year,
        month: cursor.month,
        categories: filter.categories,
        owner: filter.owner,
        maxPerDay: filter.maxPerDay,
      }),
  });

  const dayQuery = useQuery({
    queryKey: ["calendar-day", selectedDate, filter.categories.join(","), filter.owner],
    queryFn: () =>
      getDay({
        date: selectedDate as string,
        categories: filter.categories,
        owner: filter.owner,
      }),
    enabled: sheet === "day" && selectedDate != null,
  });

  // Keep y/m in the URL so the current month survives a reload and is
  // linkable. Only month-level concerns live here — the filters have their own
  // params, and mixing them would make the back button unpredictable.
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("y", String(cursor.year));
    next.set("m", String(cursor.month));
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [cursor.year, cursor.month]); // eslint-disable-line react-hooks/exhaustive-deps

  const month = monthQuery.data;
  const busiestDayItems = useMemo(() => {
    if (!month) return 0;
    return month.weeks
      .flat()
      .reduce((max, cell) => Math.max(max, cell.items.length + cell.overflow_count), 0);
  }, [month]);

  // W52.0 D-1 — the calendar was the one module page with no subtitle at all.
  // The line is computed in `@/lib/heroSummary` (pure, tested there, and out
  // of this route so it does not push the file past the 15 KB ceiling).
  const monthSubtitle = calendarMonthSummary(month);

  // W58.5 — 導航（建立 / 編輯 / 前往來源 / 開一筆週期事件）搬到
  // `useCalendarNavigation`：這一頁撞到 §3 #10 的 15 KB route 上限，而這四個
  // 動作本來就是同一組規則（見那個檔案的檔頭）。
  const { openCreate, openCycleEvent, openEdit, openItemSource } = useCalendarNavigation();

  /**
   * Errors that happen *outside* a sheet.
   *
   * `formError` is only rendered inside `EventEditor`, so writing a delete
   * failure there was a silent failure: the user can delete from the confirm
   * dialog without ever opening the editor, and the message had nowhere to
   * appear. Page-level failures get their own banner.
   */
  const [pageError, setPageError] = useState<string | null>(null);

  /**
   * W39.1 R1 (ADR-0080).
   *
   * This is the **sixth** delete entry point, and R1's first pass missed it
   * because it only looked for `ConfirmDialog` usages in files named after a
   * detail page. The month/day view deletes from its own sheet, and its dialog
   * said 「刪除後就看不到這筆安排了，此操作無法復原」 over a `deleteEvent`
   * that is a soft delete with a 7-day window and a restore route — the same
   * lie as the detail page, one screen over.
   *
   * Now: no confirm, the sheet closes, and the board owns the undo bar (same
   * arrangement as the wish kanban — the sheet is gone by the time a delete
   * succeeds, so a bar rendered inside it would vanish with it).
   */
  /**
   * R1 (ADR-0080) undo, widened by W40.1 F7 to cover the create page's rows as
   * well as a delete — and its location-state hand-off from that page. All of it
   * lives in `useCalendarUndoBar` so this file does not grow past the §3 #10
   * ceiling it is already recorded against.
   */
  const { undo, restoreMut, undoMessage } = useCalendarUndoBar(setPageError);

  function openSegment(seg: SharedSegment) {
    setSelectedDate(seg.start_date);
    setSheet("day");
  }

  return (
    <div data-testid="calendar-page">
      <PageHero
        eyebrow="共同空間"
        title="日曆"
        subtitle={monthSubtitle}
        // The primary CTA lives in the PageHero `trailing` slot, matching
        // Wishes / Diaries / Albums. It previously sat in the month-nav row
        // inside the 390px column, which is where the month controls belong.
        //
        // W42 F32/F33: the bare 44pt `+` circle became a labelled pill in ink.
        // Two defects, both measured rather than stylistic — the circle had no
        // label so 「this creates something」 rested entirely on a glyph, and
        // `bg-you-strong` is F20's **owner** step (blue = 你, rose = TA in the
        // calendar), so a global 「新增」 wearing it read as 「新增一個屬於你的
        // 東西」. `compliance-calendar.test.tsx` asserts both.
        trailing={
          <button
            type="button"
            onClick={() => openCreate(todayIso())}
            aria-label="新增事件"
            data-testid="calendar-open-create"
            className="press cta-pill hover:opacity-90 transition-opacity"
          >
            <Plus size={18} aria-hidden />
            新增
          </button>
        }
      />

      {/* The cycle view is a route (/calendar/cycle), not a sheet — see the
          route comment in App.tsx. DESIGN §2 lists it at a path, distinguished
          from the three screens marked 「（sheet）」. */}
      {/* W48.0 / ADR-0146 — **full width on mobile**, `md:` keeps Q25's centred
          390 column. The cap was applied at every width, so a 430px phone got
          ~20px of dead mist on each side and a day cell of ~40px — too narrow for
          two 18px chips and their gutter, which is what 「每一日 daycell 有足夠的
          空間可以顯示個清晰的事件 icon」 is about. Measured at 430: the cell goes
          40px → 53.4px.

          Q25 itself is untouched: it is a statement about the **desktop** layout,
          and `md:max-w-[390px]` is that statement. `compliance-calendar.test.tsx`
          asserted the cap by class name and its selector had to learn the `md:`
          prefix — the intent is the assertion, not the string. */}
      <div className="mx-auto w-full px-s-4 py-s-5 md:max-w-[390px]">
        {pageError ? (
          <p
            className="mb-s-3 rounded-sm bg-partner-tint px-s-3 py-s-2 text-body-sm text-partner-strong"
            role="alert"
            data-testid="calendar-page-error"
          >
            {pageError}
          </p>
        ) : null}
        <CalendarMonthNav
          month={cursor.month}
          year={cursor.year}
          onPrev={() => setCursor(shiftMonth(cursor.year, cursor.month, -1))}
          onNext={() => setCursor(shiftMonth(cursor.year, cursor.month, 1))}
          onToday={() => setCursor(monthOf(now))}
          onOpenFilter={() => setSheet("filter")}
        />

          {monthQuery.isLoading ? (
            <div data-testid="calendar-loading">
              <LoadingState shape="card" count={3} />
            </div>
          ) : monthQuery.isError ? (
            <ErrorState
              title="無法載入日曆"
              description="請稍候再試一次"
              onRetry={() => void monthQuery.refetch()}
            />
          ) : month ? (
            <div className="flex flex-col gap-s-4">
              {/* W58.6 / ADR-0212 —— **狀態列 = 雙欄並排卡片**。

                  左欄「共同時間 · N 段」（點它展開列表），右欄「經期 N 天後」
                  （點它進週期頁）。兩欄等高 44px、同一套外框。

                  **順序反映優先級**：共同時間講的是「**我們的**」—— 那是這個
                  模組存在的理由；而週期是**其中一個人**的身體，所以是次要位置。
                  W58.5 把它移最上方，這一輪只改同一橫列的**排法**。

                  「經期比預期晚了」的提示仍在**狀態列之上** —— 那是需要被處理
                  的事，狀態可以等。降級推算的說明在狀態列**之下**（只在降級時
                  出現）。 */}
              <CalendarStatusBar
                month={month}
                onOpenSegment={openSegment}
                onOpenCycle={() => nav("/calendar/cycle")}
                onRecord={() => openCycleEvent(todayIso())}
              />

              <div>
                <WeekdayHeader />
                <MonthGrid
                  weeks={month.weeks}
                  selectedDate={selectedDate}
                  onSelectDay={(date) => {
                    setSelectedDate(date);
                    setSheet("day");
                  }}
                  onLongPressDay={openCreate}
                />
                {/* W58.3 / W58.4 / **W58.6** — 月曆格的圖例（說明整張月曆格：
                    事件類別 + 歸屬 + 週期狀態）。W58.6 起它是**完整清單**，
                    不再隨月份變化（ADR-0214）。細節見 `MonthLegend`。 */}
                <MonthLegend />
              </div>
            </div>
          ) : null}
      </div>

      <DaySheet
        open={sheet === "day"}
        onOpenChange={(open) => setSheet(open ? "day" : "none")}
        day={dayQuery.data ?? null}
        onOpenItem={openItemSource}
        onOpenCycle={() => nav("/calendar/cycle")}
        onEditItem={openEdit}
        // W58.5 — 「標記今日經期開始」—— 與週期頁的主按鈕同一條路。
        onStartPeriod={() => openCycleEvent(selectedDate ?? todayIso())}
      />

      <FilterPanel
        open={sheet === "filter"}
        onOpenChange={(open) => setSheet(open ? "filter" : "none")}
        value={filter}
        onChange={setFilter}
        busiestDayItems={busiestDayItems}
      />

      {/*
        R1 (ADR-0080). The confirm dialog that stood here said 「刪除後就看不到
        這筆安排了，此操作無法復原」 over a soft delete with a 7-day window.
        The board owns the bar for the same reason the wish kanban does: the
        editor sheet is closed by the time the delete succeeds.
      */}
      {undo.item ? (
        <div
          data-testid="calendar-undo-slot"
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-40 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message={undoMessage(undo.item)}
              onUndo={() => restoreMut.mutate()}
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
